/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from "react";
// Holiday / leave data, mapped the same way pages/timesheet/index.tsx does it
import {
    HolidayRow,
    LeaveRow,
    TimelineMarker,
    TimelineMarkerKind,
    buildMarkerMap,
    mapHolidaysToMarkers,
    mapLeavesToMarkers,
    useTimelineCalendar,
} from "./timelineCalendar";

/* ────────────────────────────────────────────────────────────
   Public types
──────────────────────────────────────────────────────────── */
export type TimelineStatusKey = "ACTIVE" | "IDLE" | "OFFLINE" | "OTHER";

export interface TimelineSegmentInput {
    status: string;
    start: string;
    end?: string | null;
    durationSeconds?: number;
    sessionCount?: number;
}

export interface TimelineDayInput {
    date: string;
    firstActivity?: string | null;
    lastActivity?: string | null;
    totalTime?: number;
    activeTime?: number;
    idleTime?: number;
    offlineTime?: number;
    segments?: TimelineSegmentInput[];
}

export interface TimelineProps {
    days: TimelineDayInput[];
    title?: string;
    subtitle?: string;
    showDayFilter?: boolean;
    showLegend?: boolean;
    emptyMessage?: string;
    className?: string;
    formatDuration?: (seconds: number) => string;
    /**
     * Employee email. When provided, holidays and that employee's leaves are
     * loaded automatically (GET /api/holiday/{year} and
     * GET /api/leave/data/{year}?email=…) for the years the range covers.
     */
    email?: string | null;
    /** Pre-fetched holiday rows — supplied instead of letting the Timeline fetch. */
    holidays?: HolidayRow[];
    /** Pre-fetched leave rows — supplied instead of letting the Timeline fetch. */
    leaves?: LeaveRow[];
    /** Set false to render activity only (no holiday / leave ribbons). */
    showCalendarMarkers?: boolean;
    /**
     * Add rows for holiday / leave dates that have no tracked activity.
     * Off by default: the day list stays exactly as the activity data defines it.
     */
    includeMarkerOnlyDays?: boolean;
}

/* ────────────────────────────────────────────────────────────
   Constants
──────────────────────────────────────────────────────────── */
const DAY_MINUTES = 24 * 60;
const MIN_WINDOW_MINUTES = 4 * 60;
const TICK_STEPS = [15, 30, 60, 120, 180, 240];
const MAX_TICKS = 9;
const DEFAULT_TRACK_WIDTH = 640;
const LABEL_MIN_GAP_PX = 62;

const STATUS_META: Record<TimelineStatusKey, { label: string; bar: string; dot: string }> = {
    ACTIVE: { label: "Active", bar: "bg-green-500", dot: "bg-green-500" },
    IDLE: { label: "Idle", bar: "bg-amber-400", dot: "bg-amber-400" },
    OFFLINE: { label: "Offline", bar: "bg-slate-300 dark:bg-slate-600", dot: "bg-slate-300 dark:bg-slate-600" },
    OTHER: { label: "Other", bar: "bg-slate-200 dark:bg-slate-700", dot: "bg-slate-200 dark:bg-slate-700" },
};

const LEGEND_KEYS: TimelineStatusKey[] = ["ACTIVE", "IDLE", "OFFLINE"];

/**
 * Holiday / leave colours are deliberately outside the activity palette
 * (green / amber / slate) so a ribbon can never be mistaken for tracked time.
 */
const MARKER_META: Record<
    TimelineMarkerKind,
    { label: string; ribbon: string; dot: string; chip: string }
> = {
    HOLIDAY: {
        label: "Holiday",
        ribbon: "bg-violet-500",
        dot: "bg-violet-500",
        chip: "bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200",
    },
    LEAVE: {
        label: "Leave",
        ribbon: "bg-rose-500",
        dot: "bg-rose-500",
        chip: "bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
    },
};

const UNTRACKED_PATTERN: React.CSSProperties = {
    backgroundImage:
        "repeating-linear-gradient(45deg, rgba(148,163,184,0.16) 0px, rgba(148,163,184,0.16) 3px, transparent 3px, transparent 8px)",
};

/**
 * Half-day leaves are drawn across the whole day with a striped ribbon instead
 * of half a ribbon: the API only reports 0.5 of a day (never which half), so
 * anchoring it to a clock position would invent information.
 */
const HALF_DAY_PATTERN: React.CSSProperties = {
    backgroundImage:
        "repeating-linear-gradient(45deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 4px, transparent 4px, transparent 9px)",
};

/* ────────────────────────────────────────────────────────────
   Helpers
──────────────────────────────────────────────────────────── */
const defaultFormatDuration = (secs?: number | null) => {
    if (!secs || secs <= 0) return "0m";
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs % 3600) / 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const parseTs = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;

const formatClock = (d: Date | null) =>
    d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--";

const formatTick = (mins: number) => {
    const hour24 = Math.floor(mins / 60) % 24;
    const minute = Math.round(mins % 60);
    const suffix = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return minute === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(minute).padStart(2, "0")}`;
};

const toStatusKey = (status?: string): TimelineStatusKey => {
    const key = (status || "").toUpperCase();
    return key === "ACTIVE" || key === "IDLE" || key === "OFFLINE" ? key : "OTHER";
};

const anchor = (percent: number): React.CSSProperties => ({
    left: `${percent}%`,
    transform: percent < 5 ? "translateX(0)" : percent > 95 ? "translateX(-100%)" : "translateX(-50%)",
});

/* ────────────────────────────────────────────────────────────
   Internal shapes
──────────────────────────────────────────────────────────── */
interface PlacedSegment {
    key: TimelineStatusKey;
    rawStatus: string;
    startAt: Date;
    endAt: Date | null;
    startMin: number;
    endMin: number;
    durationSeconds: number;
    sessionCount: number;
}

interface TimelineRow {
    day: TimelineDayInput;
    dayLabel: { weekday: string; date: string };
    segments: PlacedSegment[];
    first: Date | null;
    last: Date | null;
    /** Holidays / leaves falling on this day. */
    markers: TimelineMarker[];
}

interface TimeLabel {
    pos: number;
    text: string;
}

const dayLabelParts = (date: string) => {
    const d = parseTs(`${date}T00:00:00`);
    if (!d) return { weekday: date, date: "" };
    return {
        weekday: d.toLocaleDateString([], { weekday: "short" }),
        date: d.toLocaleDateString([], { day: "2-digit", month: "short" }),
    };
};

const placeLabels = (candidates: TimeLabel[], minGapPercent: number): TimeLabel[] => {
    if (candidates.length === 0) return [];
    const sorted = [...candidates].sort((a, b) => a.pos - b.pos);
    const closing = sorted[sorted.length - 1];

    const kept: TimeLabel[] = [];
    sorted.forEach((candidate) => {
        if (kept.length === 0 || candidate.pos - kept[kept.length - 1].pos >= minGapPercent) {
            kept.push(candidate);
        }
    });

    if (kept[kept.length - 1] !== closing) {
        while (kept.length > 1 && closing.pos - kept[kept.length - 1].pos < minGapPercent) kept.pop();
        kept.push(closing);
    }
    return kept;
};

/* ────────────────────────────────────────────────────────────
   Component
──────────────────────────────────────────────────────────── */
const Timeline: React.FC<TimelineProps> = ({
    days,
    title = "Activity Timeline",
    subtitle = "Active, idle and offline periods plotted on the clock.",
    showDayFilter = true,
    showLegend = true,
    emptyMessage = "No activity data available for the selected period.",
    className = "",
    formatDuration = defaultFormatDuration,
    email,
    holidays,
    leaves,
    showCalendarMarkers = true,
    includeMarkerOnlyDays = false,
}) => {
    const [selectedDate, setSelectedDate] = useState<string>("All");
    const [hovered, setHovered] = useState<{ date: string; index: number } | null>(null);
    const [trackWidth, setTrackWidth] = useState<number>(DEFAULT_TRACK_WIDTH);
    const trackRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const node = trackRef.current;
        if (!node || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width;
            if (width) setTrackWidth(width);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    /* Years the visible range covers — a range can straddle a year boundary. */
    const years = useMemo(() => {
        const found = new Set<number>();
        (days || []).forEach((day) => {
            const year = Number(String(day?.date || "").slice(0, 4));
            if (year) found.add(year);
        });
        return Array.from(found);
    }, [days]);

    /* Holidays / leaves: caller-supplied rows win, otherwise fetch by email. */
    const fetched = useTimelineCalendar({
        email,
        years,
        fetchHolidays: showCalendarMarkers && !holidays,
        fetchLeaves: showCalendarMarkers && !leaves,
    });

    const markerMap = useMemo(() => {
        if (!showCalendarMarkers) return {};

        const holidayMarkers = holidays ? mapHolidaysToMarkers(holidays) : fetched.holidayMarkers;
        const leaveMarkers = leaves ? mapLeavesToMarkers(leaves) : fetched.leaveMarkers;

        return buildMarkerMap(holidayMarkers, leaveMarkers);
    }, [showCalendarMarkers, holidays, leaves, fetched.holidayMarkers, fetched.leaveMarkers]);

    /**
     * Which days get a row. NOTE: this is the original activity-only rule —
     * holidays and leaves decorate the days that are already listed and never
     * add, remove or reorder rows, so the day filter keeps behaving exactly as
     * it did before markers existed.
     *
     * `includeMarkerOnlyDays` opts in to extra rows for holiday / leave dates
     * that have no tracked activity (off by default).
     */
    const activeDays = useMemo(() => {
        const dayList = days || [];
        const kept = dayList.filter(
            (d) => (d.totalTime || 0) > 0 || (d.segments || []).length > 0
        );

        let combined = kept;

        if (includeMarkerOnlyDays) {
            // Only dates inside the requested range, so nothing outside the filter appears
            const dates = dayList.map((d) => d.date).filter(Boolean).sort();
            const rangeStart = dates[0];
            const rangeEnd = dates[dates.length - 1];

            if (rangeStart && rangeEnd) {
                const known = new Set(kept.map((d) => d.date));
                const markerOnly: TimelineDayInput[] = Object.keys(markerMap)
                    .filter(
                        (dateKey) => !known.has(dateKey) && dateKey >= rangeStart && dateKey <= rangeEnd
                    )
                    .map((dateKey) => ({ date: dateKey, totalTime: 0, segments: [] }));

                combined = [...kept, ...markerOnly];
            }
        }

        // Sort in decreasing order (newest date first)
        return [...combined].sort((a, b) => (a.date < b.date ? 1 : -1));
    }, [days, markerMap, includeMarkerOnlyDays]);

    useEffect(() => {
        if (selectedDate !== "All" && !activeDays.some((d) => d.date === selectedDate)) {
            setSelectedDate("All");
        }
    }, [activeDays, selectedDate]);

    const rows: TimelineRow[] = useMemo(() => {
        const visible =
            selectedDate === "All" ? activeDays : activeDays.filter((d) => d.date === selectedDate);

        return visible.map((day) => {
            const segments = (day.segments || []).reduce<PlacedSegment[]>((acc, seg) => {
                const startAt = parseTs(seg.start);
                if (!startAt) return acc;

                const endAt = parseTs(seg.end);
                const startMin = minutesOfDay(startAt);
                const rawEndMin = endAt
                    ? minutesOfDay(endAt)
                    : startMin + (seg.durationSeconds || 0) / 60;

                const endMin = rawEndMin < startMin ? DAY_MINUTES : Math.min(rawEndMin, DAY_MINUTES);

                acc.push({
                    key: toStatusKey(seg.status),
                    rawStatus: seg.status,
                    startAt,
                    endAt,
                    startMin,
                    endMin,
                    durationSeconds: seg.durationSeconds || Math.round((endMin - startMin) * 60),
                    sessionCount: seg.sessionCount || 0,
                });
                return acc;
            }, []);

            segments.sort((a, b) => a.startMin - b.startMin);

            return {
                day,
                dayLabel: dayLabelParts(day.date),
                segments,
                first: parseTs(day.firstActivity),
                last: parseTs(day.lastActivity),
                markers: markerMap[day.date] || [],
            };
        });
    }, [activeDays, selectedDate, markerMap]);

    const axis = useMemo(() => {
        let earliest = Number.POSITIVE_INFINITY;
        let latest = Number.NEGATIVE_INFINITY;

        rows.forEach((row) => {
            row.segments.forEach((seg) => {
                earliest = Math.min(earliest, seg.startMin);
                latest = Math.max(latest, seg.endMin);
            });
            if (row.first) earliest = Math.min(earliest, minutesOfDay(row.first));
            if (row.last) latest = Math.max(latest, minutesOfDay(row.last));
        });

        if (!Number.isFinite(earliest) || !Number.isFinite(latest)) {
            earliest = 9 * 60;
            latest = 18 * 60;
        }

        let start = Math.max(0, Math.floor(earliest / 60) * 60);
        let end = Math.min(DAY_MINUTES, Math.ceil(latest / 60) * 60);
        if (end - start < MIN_WINDOW_MINUTES) {
            end = Math.min(DAY_MINUTES, start + MIN_WINDOW_MINUTES);
            start = Math.max(0, end - MIN_WINDOW_MINUTES);
        }

        const span = Math.max(1, end - start);
        const step = TICK_STEPS.find((s) => span / s <= MAX_TICKS) ?? TICK_STEPS[TICK_STEPS.length - 1];
        const ticks: number[] = [];
        for (let m = start; m <= end; m += step) ticks.push(m);

        return { start, end, span, ticks };
    }, [rows]);

    const pos = (mins: number) =>
        Math.min(100, Math.max(0, ((mins - axis.start) / axis.span) * 100));

    const minGapPercent = (LABEL_MIN_GAP_PX / Math.max(trackWidth, 160)) * 100;

    // Use consistent height regardless of number of days
    const barHeight = "h-8";

    /* Legend only advertises marker kinds that are actually present */
    const presentMarkerKinds = useMemo(() => {
        const kinds = new Set<TimelineMarkerKind>();
        rows.forEach((row) => row.markers.forEach((marker) => kinds.add(marker.kind)));
        return (["HOLIDAY", "LEAVE"] as TimelineMarkerKind[]).filter((kind) => kinds.has(kind));
    }, [rows]);

    const markerLabel = (marker: TimelineMarker) => {
        const meta = MARKER_META[marker.kind];
        const title = marker.title || meta.label;
        return marker.halfDay ? `${title} · ½ day` : title;
    };

    /** Ribbon lane above the activity bar: one ribbon per holiday / leave. */
    const renderMarkerRibbons = (row: TimelineRow) => {
        if (row.markers.length === 0) return null;

        return (
            <div className="flex flex-col gap-[3px] mb-1.5">
                {row.markers.map((marker, i) => {
                    const meta = MARKER_META[marker.kind];
                    // The marker's own date is in the tooltip so a mismatch with
                    // the row it sits on is immediately visible
                    const title = `${meta.label}: ${markerLabel(marker)}${marker.status ? ` (${marker.status})` : ""
                        } · ${marker.date}`;

                    return (
                        <div
                            key={`${marker.kind}-${marker.id ?? i}`}
                            className={`relative h-2 rounded-full overflow-hidden ${meta.ribbon}`}
                            title={title}
                            role="img"
                            aria-label={title}
                        >
                            {marker.halfDay && (
                                <span className="absolute inset-0" style={HALF_DAY_PATTERN} />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTimeLabels = (row: TimelineRow) => {
        const candidates: TimeLabel[] = [];

        if (row.segments.length > 0) {
            row.segments.forEach((seg) => {
                candidates.push({ pos: pos(seg.startMin), text: formatClock(seg.startAt) });
            });
            const lastSeg = row.segments[row.segments.length - 1];
            candidates.push({
                pos: pos(lastSeg.endMin),
                text: formatClock(lastSeg.endAt) !== "--" ? formatClock(lastSeg.endAt) : formatClock(row.last),
            });
        } else {
            if (row.first) candidates.push({ pos: pos(minutesOfDay(row.first)), text: formatClock(row.first) });
            if (row.last) candidates.push({ pos: pos(minutesOfDay(row.last)), text: formatClock(row.last) });
        }

        if (candidates.length === 0) return null;

        const opening = candidates[0];
        const closing = candidates[candidates.length - 1];

        if (candidates.length > 1 && closing.pos - opening.pos < minGapPercent) {
            return (
                <span
                    className="absolute top-0 text-[11px] leading-4 text-gray-500 dark:text-gray-400 whitespace-nowrap"
                    style={anchor((opening.pos + closing.pos) / 2)}
                >
                    {opening.text} – {closing.text}
                </span>
            );
        }

        return placeLabels(candidates, minGapPercent).map((label) => (
            <React.Fragment key={`${label.pos}-${label.text}`}>
                <span
                    className="absolute top-0 w-px h-1.5 -translate-x-1/2 bg-gray-300 dark:bg-gray-600"
                    style={{ left: `${label.pos}%` }}
                />
                <span
                    className="absolute top-[5px] text-[11px] leading-4 text-gray-500 dark:text-gray-400 whitespace-nowrap"
                    style={anchor(label.pos)}
                >
                    {label.text}
                </span>
            </React.Fragment>
        ));
    };

    return (
        <div
            className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 sm:p-6 ${className}`}
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                <div>
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{title}</h3>
                    {subtitle && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    {showLegend && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                            {LEGEND_KEYS.map((key) => (
                                <span
                                    key={key}
                                    className="flex items-center gap-1.5 text-[12px] text-gray-600 dark:text-gray-300"
                                >
                                    <span className={`w-2.5 h-2.5 rounded-full ${STATUS_META[key].dot}`} />
                                    {STATUS_META[key].label}
                                </span>
                            ))}
                            <span className="flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400">
                                <span
                                    className="w-2.5 h-2.5 rounded-full border border-gray-300 dark:border-gray-600"
                                    style={UNTRACKED_PATTERN}
                                />
                                Not tracked
                            </span>

                            {/* Holiday / leave keys appear only when such days are in range */}
                            {presentMarkerKinds.map((kind) => (
                                <span
                                    key={kind}
                                    className="flex items-center gap-1.5 text-[12px] text-gray-600 dark:text-gray-300"
                                >
                                    <span className={`w-2.5 h-2.5 rounded-full ${MARKER_META[kind].dot}`} />
                                    {MARKER_META[kind].label}
                                </span>
                            ))}
                        </div>
                    )}

                    {showDayFilter && activeDays.length > 0 && (
                        <>
                            <label htmlFor="timeline-day-filter" className="sr-only">
                                Filter timeline by day
                            </label>
                            <select
                                id="timeline-day-filter"
                                className="h-9 pl-3 pr-8 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[13px] font-medium text-gray-700 dark:text-gray-300 outline-none focus:ring-2 focus:ring-blue-500"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            >
                                <option value="All">All days ({activeDays.length})</option>
                                {activeDays.map((day) => {
                                    const parts = dayLabelParts(day.date);
                                    return (
                                        <option key={day.date} value={day.date}>
                                            {parts.weekday}, {parts.date}
                                        </option>
                                    );
                                })}
                            </select>
                        </>
                    )}
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
                    {emptyMessage}
                </div>
            ) : (
                <div className="overflow-x-auto -mx-1 px-1">
                    <div className="min-w-[560px]">
                        {/* Shared hour ruler */}
                        <div className="flex items-end mb-1">
                            <div className="w-[58px] shrink-0" aria-hidden="true" />
                            <div ref={trackRef} className="relative w-full h-4 flex-1">
                                {axis.ticks.map((tick) => (
                                    <span
                                        key={tick}
                                        className="absolute top-0 text-[11px] leading-4 font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap"
                                        style={anchor(pos(tick))}
                                    >
                                        {formatTick(tick)}
                                    </span>
                                ))}
                            </div>
                            <div className="w-[74px] shrink-0" aria-hidden="true" />
                        </div>

                        {/* Rows — Added pb-6 padding to ensure absolute popups don't trigger scrollbars */}
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60 pb-6 pt-3 space-y-6">
                            {rows.map((row, rowIndex) => {
                                const { day } = row;
                                const total = day.totalTime || 0;
                                const activePercent =
                                    total > 0 && day.activeTime ? Math.round((day.activeTime / total) * 100) : null;
                                const hoveredSeg =
                                    hovered?.date === day.date ? row.segments[hovered.index] : undefined;

                                // Dynamically render tooltip upward if it's the very last row (and multiple rows exist)
                                const isLastRow = rowIndex === rows.length - 1 && rows.length > 1;

                                return (
                                    <div key={day.date} className="flex flex-col gap-1.5 pb-2">
                                        {/* Day Header - Contains Date on left, Breakdown on Right */}
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 shrink-0">
                                                    {row.dayLabel.weekday}, {row.dayLabel.date}
                                                </span>

                                                {/* Names the ribbons below (occasion / leave type) */}
                                                {row.markers.map((marker, i) => (
                                                    <span
                                                        key={`chip-${marker.kind}-${marker.id ?? i}`}
                                                        className={`px-1.5 py-[1px] rounded text-[10.5px] font-semibold whitespace-nowrap truncate max-w-[170px] ${MARKER_META[marker.kind].chip}`}
                                                        title={markerLabel(marker)}
                                                    >
                                                        {markerLabel(marker)}
                                                    </span>
                                                ))}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-gray-500 dark:text-gray-400">
                                                {(day.activeTime ?? 0) > 0 && (
                                                    <span className="flex items-center gap-1.5">
                                                        <span className={`w-2 h-2 rounded-full ${STATUS_META['ACTIVE'].dot}`} />
                                                        {formatDuration(day.activeTime ?? 0)}
                                                    </span>
                                                )}
                                                {(day.idleTime ?? 0) > 0 && (
                                                    <span className="flex items-center gap-1.5">
                                                        <span className={`w-2 h-2 rounded-full ${STATUS_META['IDLE'].dot}`} />
                                                        {formatDuration(day.idleTime ?? 0)}
                                                    </span>
                                                )}
                                                {(day.offlineTime ?? 0) > 0 && (
                                                    <span className="flex items-center gap-1.5">
                                                        <span className={`w-2 h-2 rounded-full ${STATUS_META['OFFLINE'].dot}`} />
                                                        {formatDuration(day.offlineTime ?? 0)}
                                                    </span>
                                                )}
                                                <span className="font-semibold text-gray-800 dark:text-gray-200 ml-1 text-[12.5px]">
                                                    {formatDuration(total)} total
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            {/* Left margin spacing filler */}
                                            <div className="w-[58px] shrink-0" aria-hidden="true" />

                                            {/* Track */}
                                            <div className="relative w-full flex-1">
                                                {/* Holiday / leave ribbons, aligned to the same day width */}
                                                {renderMarkerRibbons(row)}

                                                <div
                                                    className={`relative ${barHeight} rounded-md overflow-hidden bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700`}
                                                    style={UNTRACKED_PATTERN}
                                                    role="img"
                                                    aria-label={`${row.dayLabel.weekday} ${row.dayLabel.date}: ${formatDuration(total)} tracked between ${formatClock(row.first)} and ${formatClock(row.last)}`}
                                                >
                                                    {/* Vertical grid lines inside the track */}
                                                    {axis.ticks.slice(1, -1).map((tick) => (
                                                        <span
                                                            key={tick}
                                                            className="absolute top-0 bottom-0 w-px bg-gray-200/80 dark:bg-gray-700/80"
                                                            style={{ left: `${pos(tick)}%` }}
                                                        />
                                                    ))}

                                                    {/* Placed Segments */}
                                                    {row.segments.map((seg, i) => {
                                                        const left = pos(seg.startMin);
                                                        const width = Math.max(pos(seg.endMin) - left, 0.3);
                                                        const isHovered = hovered?.date === day.date && hovered?.index === i;

                                                        return (
                                                            <div
                                                                key={`${seg.startMin}-${i}`}
                                                                style={{ left: `${left}%`, width: `${width}%`, minWidth: 2 }}
                                                                className={`absolute top-0 bottom-0 ${STATUS_META[seg.key].bar} border-r border-white/30 dark:border-gray-900/30 cursor-pointer transition-opacity ${isHovered ? "opacity-100 z-10" : "opacity-95 hover:opacity-100"}`}
                                                                onMouseEnter={() => setHovered({ date: day.date, index: i })}
                                                                onMouseLeave={() => setHovered(null)}
                                                            />
                                                        );
                                                    })}

                                                    {row.segments.length === 0 && (
                                                        <div className="absolute inset-0 flex items-center justify-center text-[12px] text-gray-400 dark:text-gray-500">
                                                            No segments recorded
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Permanent Labels placed below the bar */}
                                                <div className="relative h-6 mt-1.5">{renderTimeLabels(row)}</div>

                                                {/* Tooltip on hover */}
                                                {hoveredSeg && (
                                                    <div
                                                        className={`absolute z-30 pointer-events-none ${isLastRow ? "bottom-full mb-2.5" : "top-full mt-2.5"}`}
                                                        style={anchor((pos(hoveredSeg.startMin) + pos(hoveredSeg.endMin)) / 2)}
                                                    >
                                                        <div className="px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white shadow-xl whitespace-nowrap text-[11px] relative">
                                                            <div className={`absolute left-1/2 -translate-x-1/2 border-4 border-transparent ${isLastRow ? "top-full border-t-gray-900 dark:border-t-gray-700" : "bottom-full border-b-gray-900 dark:border-b-gray-700"
                                                                }`} />
                                                            <span className="inline-flex items-center gap-1.5 font-semibold">
                                                                <span className={`w-2 h-2 rounded-full ${STATUS_META[hoveredSeg.key].dot}`} />
                                                                {STATUS_META[hoveredSeg.key].label}
                                                            </span>
                                                            <span className="text-gray-300">
                                                                {" · "}
                                                                {formatClock(hoveredSeg.startAt)} – {formatClock(hoveredSeg.endAt)}
                                                                {" · "}
                                                                {formatDuration(hoveredSeg.durationSeconds)}
                                                                {hoveredSeg.sessionCount > 0 && (
                                                                    <>
                                                                        {" · "}
                                                                        {hoveredSeg.sessionCount} session{hoveredSeg.sessionCount > 1 ? "s" : ""}
                                                                    </>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right margin spacing filler for totals rail */}
                                            <div className="w-[74px] shrink-0 text-right leading-tight">
                                                <p className="text-[12px] font-semibold text-gray-800 dark:text-gray-200">
                                                    {formatDuration(total)}
                                                </p>
                                                {activePercent !== null && (
                                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                                        {activePercent}% active
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Timeline;